<!-- 工作区顶部信息 - 工作区人员显示 -->
<template>
  <div class="left">
    <div class="left-label">工作区人员：</div>
    <!-- 当前工作区创建者才允许邀请成员 -->
    <base-button v-if="showUpUserBtn" class="workbench-btn user-btn" type="default" @click="goPay('work')">
      升级人数
    </base-button>
    <!-- 没有权限占位 -->
    <div v-else style="height: 20px"></div>
  </div>

  <div>
    <template v-if="teamStore.isInitStaticsData">
      <div class="user">
        <!-- 成员头像 -->
        <img
          v-for="item in [...(teamUsers?.member_pics || [])].splice(0, 4)"
          :key="item"
          class="user-list"
          :src="item"
          @click="showWorkUserFn"
        />
        <!-- 成员头像最多显示4个，超出显示省略号 -->
        <div
          v-if="teamUsers && teamUsers.member_pics && teamUsers.member_pics.length > 4"
          class="user-list user-list_more"
          @click="showWorkUserFn"
        >
          <IconMyWorkbenchUserMore></IconMyWorkbenchUserMore>
        </div>
        <!-- 当前工作区创建者/管理员才允许邀请成员 -->
        <div
          v-if="(teamStore.isWorkbenchOwner || teamStore.isWorkbenchManager) && !teamStore.isTeamUserDisabled"
          class="user-add"
          @click="openWorkInvitationDialog('work')"
        >
          <IconMyWorkbenchUserAdd></IconMyWorkbenchUserAdd>
        </div>
      </div>
      <div class="user-number">启用人员 {{ teamUsers?.member_enable_num }}/{{ teamRights?.cnt }}</div>
    </template>
    <!-- 骨架屏 -->
    <div v-else class="skeleton-main">
      <workbench-info-skeleton :width="108" :height="22"></workbench-info-skeleton>
      <workbench-info-skeleton :width="108" :height="22"></workbench-info-skeleton>
    </div>
  </div>

  <!-- 工作区成员详情弹窗 -->
  <work-user-dialog
    v-if="showWorkUserDialog"
    @on-work-user-dialog-closed="onWorkUserDialogClosed"
    @open-work-invitation-dialog="
      () => {
        openWorkInvitationDialog('user');
      }
    "
    @user-max-dialog-open="userMaxDialogOpen"
    @init-team-config="initTeamConfig"
  ></work-user-dialog>

  <!-- 邀请成员弹窗 -->
  <work-invitation-dialog
    v-if="showWorkInvitation"
    :showWorkUserDialog="showWorkUserDialog"
    @on-work-invitation-dialog-closed="onWorkInvitationDialogClosed"
  ></work-invitation-dialog>

  <!-- 启用成员上限弹窗 -->
  <el-dialog
    v-model="userMaxDialog"
    customClass="dialog-common workuser-max-dialog"
    appendToBody
    :showClose="false"
    :closeOnClickModal="false"
  >
    <!-- 头部 -->
    <template #header>
      <div class="title">
        <IconMyIconWarning></IconMyIconWarning>
        <span>成员超限</span>
      </div>
      <IconMyIconModalClose class="icon-close" @click="userMaxDialog = false"></IconMyIconModalClose>
    </template>

    <!-- 内容 -->
    <template #default>
      <div>当前工作区仅支持{{ teamRights?.cnt }}人协作，请升级套餐人数或禁用其他成员后再操作。</div>
    </template>

    <!-- 操作按钮 -->
    <template #footer>
      <base-button type="primary" @click="goPay('user')">升级</base-button>
      <base-button type="default" @click="userMaxDialog = false">取消</base-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue';
import { usePTeamStore } from '@/store';
import BaseButton from '@/components/base-button.vue';
import { apiGetPaymentConfig } from '@vas2t-lowcode/api';
import {
  dwClickMember,
  dwClickAddMember,
  dwClickUpgradeMember,
  dwMemberAdd,
  dwMemberMaxTip,
  dwMemberUpgrade,
} from './workbench-info-dw';
import { getWorkbenchEmitter } from './workbench-info.tools';
import { getPositionRule } from '@/utils';

const WorkbenchInfoSkeleton = defineAsyncComponent(() => import('./workbench-info-skeleton.vue'));
const WorkUserDialog = defineAsyncComponent(() => import('./workbench-info-workuser-dialog.vue'));
const WorkInvitationDialog = defineAsyncComponent(() => import('./workbench-invitation.vue'));

const emit = defineEmits<{
  (e: 'set-pay-config-to-pay', data: any): void;
  (e: 'init-team-config'): void;
}>();

const teamStore = usePTeamStore();

// 工作区成员详情弹窗
const showWorkUserDialog = ref(false);
// 邀请成员弹窗
const showWorkInvitation = ref(false);
// 成员超限弹窗
const userMaxDialog = ref(false);

/** 工作台成员相关信息 */
const teamUsers = computed(() => {
  return teamStore.statics?.team_users;
});

/** 当前工作区付费信息 */
const teamRights = computed(() => {
  return teamStore.statics?.team_rights;
});

/** 是否显示升级人数按钮 */
const showUpUserBtn = computed(() => {
  // 非人数上限 && 是当前工作区拥有者
  return (
    !teamRights.value?.is_top_member_cnt &&
    (teamStore.isWorkbenchOwner || teamStore.isWorkbenchManager) &&
    !teamStore.isTeamUserDisabled
  );
});

/**
 * 显示成员弹窗
 */
function showWorkUserFn() {
  // 解决当前用户被禁用后，自动刷新获取到最新工作区问题
  // 更新工作区数据
  // getWorkbenchEmitter().emit('get-team-config');

  showWorkUserDialog.value = true;
  // 埋点
  dwClickMember();
}

/**
 * 升级人数
 */
async function goPay(type: string) {
  // 埋点 区分工作区|成员
  type && type === 'work' ? dwClickUpgradeMember() : dwMemberUpgrade();

  userMaxDialog.value = false;

  let payconfigStr = '';
  try {
    const { data } = await apiGetPaymentConfig();
    payconfigStr = data.pure_team_mode || '';
  } catch (error) {
    // 获取支付配置失败
    console.log(error);
    return;
  }

  const payConfig = {
    isClient: false,
    // 运营/产品 提供
    from_name: 'vas2t',
    csource: payconfigStr,
    position: getPositionRule({
      create_time: teamRights.value?.create_time || 0,
      member_level: teamRights.value?.member_level || '',
      position: type && type === 'work' ? 'upgrade_member2' : 'upgrade_member3',
    }),
    // 接口获取
    payconfig: payconfigStr,
    // 支付配置
    extra: '',
  };
  //  默认是升级逻辑
  const extra = {
    purchase_type: 3,
    team_id: teamStore.statics?.curr_team_info?.team_id,
    cur_member_id: teamRights.value?.member_id,
    cur_duration_expires: teamRights.value?.end_time,
    cur_numeric_value: teamRights.value?.cnt,
  };

  // 新购  新用户 || 权益过期
  if (teamRights.value?.member_id === 0 || teamRights.value?.is_end_time_expired) {
    extra.purchase_type = 1;
  }

  payConfig.extra = JSON.stringify(extra);

  // 设置支付参数,调起支付
  emit('set-pay-config-to-pay', payConfig);
}

/**
 * 关闭工作区成员详情弹窗
 */
function onWorkUserDialogClosed() {
  showWorkUserDialog.value = false;
}

/**
 * 开启用户超员提醒弹窗
 */
function userMaxDialogOpen() {
  userMaxDialog.value = true;
  // 成员超限埋点
  dwMemberMaxTip();
}

/**
 * 开启邀请弹窗
 */
function openWorkInvitationDialog(type: string) {
  // 更新工作区数据
  getWorkbenchEmitter().emit('get-team-config');

  // 当前启用人数大于等于套餐人数需要提醒用户升级
  if (
    teamUsers.value &&
    teamUsers.value.member_enable_num &&
    teamRights.value &&
    teamRights.value.cnt &&
    teamUsers.value.member_enable_num >= teamRights.value.cnt
  ) {
    userMaxDialogOpen();
    return;
  }

  // 埋点 区分工作台 和 成员触发
  type === 'work' ? dwClickAddMember() : dwMemberAdd();

  showWorkInvitation.value = true;
}

/**
 * 关闭邀请弹窗
 */
function onWorkInvitationDialogClosed() {
  showWorkInvitation.value = false;
}

/**
 * 更新工作区信息
 */
function initTeamConfig() {
  emit('init-team-config');
}
</script>

<style lang="scss" scoped>
.left {
  margin-right: 14px;
  &-label {
    font-size: 12px;
    line-height: 22px;
    margin-bottom: 10px;
  }
}

.user {
  display: flex;
  margin-bottom: 10px;
  min-width: 120px;

  &-list {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #f5f7f9;
    cursor: pointer;
    &:not(:first-child) {
      margin-left: -6px;
    }

    // 更多省略号
    &.user-list_more {
      background: #e5e5e5;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &-add {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #f5f7f9;
    margin-left: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e4e6eb;

    &:hover {
      background: #f3f4f5;
      border: 1px solid #f5f7f9;
    }

    &:active {
      background: #d7d9de;
      border: 1px solid #f5f7f9;
    }
  }

  &-number {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    line-height: 22px;
  }
}

:deep(.user-btn) {
  width: 64px;
}

.skeleton-main {
  > div:nth-of-type(1) {
    margin-bottom: 10px;
  }
}
</style>

<style lang="scss">
// 成员超限弹窗
.workuser-max-dialog {
  --el-dialog-width: 420px;
  .el-dialog {
    &__header {
      display: flex;
      align-items: center;
      position: relative;
      padding: 18px 24px 12px;
      .title {
        flex: none;
        display: flex;
        align-items: center;
        height: 28px;
        font-size: 20px;
        line-height: 28px;
        > span {
          margin-left: 8px;
          color: var(--lowcode-warning-01);
        }
      }
      .icon-close {
        position: absolute;
        right: 24px;
        top: 24px;
      }
    }

    &__body {
      height: auto;
      display: flex;
      flex-direction: column;
      color: var(--lowcode-text-03);
      line-height: 22px;
      font-size: 14px;
    }
  }
}
</style>
